/**
 * `/metal-resources/references/glossary/`: the searchable glossary of metal terms, built for both
 * brands (there is no `NEXT_PUBLIC_COMPANY` check here).
 *
 * At build time `getStaticProps` fetches every glossary term in all three languages with
 * `src/contentful/glossary/getContentfulGlossary.ts` (already filtered to this brand and ordered by
 * title; a Contentful failure fails the build). The page renders
 * `src/components/Glossary/Page/Page.tsx` with the current language's terms, and adds a
 * schema.org `DefinedTermSet` JSON-LD block listing every term, for SEO.
 *
 * The hero and SEO copy come from
 * `src/content/shared/metal-resources/references/glossary/index.ts`. `GlossaryPageContent` here
 * holds the contact panel and search labels, and is also imported by the FAQ page
 * (`src/pages/metal-resources/references/faq/index.tsx`).
 *
 * Smoke-tested, with no terms, by `src/unit-test/MetalResources/Glossary.spec.tsx`.
 */
import React from "react";
import { LanguageContent } from "@components/Shared/model/LanguageContent";
import { useApplication } from "@ryerson/frontend.application";
import GlossaryPage, { GlossaryPageSetup } from "@components/Glossary/Page/Page";
import MetaHead from "@components/Shared/model/MetaHead";
import { useHasMounted } from "@components/Hooks";
import { allContentfulHeaderCalls, HeaderPageProps } from "@ryerson/frontend.contentful";
import HeaderLayout from "@components/NextLayouts/HeaderLayout";
import { getHeaderData } from "@components/NextLayouts/nextUtilities";
import getContentfulGlossary from "@contentful/glossary/getContentfulGlossary";
import { ProcessedContentfulGlossaryFieldsByLanguage } from "@contentful/glossary/types";
import {
	GlossaryHeroContent,
	GlossaryMetaObject,
} from "@content/shared/metal-resources/references/glossary";

/**
 * Contact-panel and search-result labels for the glossary and FAQ pages, per language. A
 * different symbol from `content/shared/metal-resources/references/glossary` →
 * `GlossaryPageContent`, which shares the name.
 */
export const GlossaryPageContent: LanguageContent<GlossaryPageSetup> = {
	en: {
		contactLabel: "Do you have an unanswered question?",
		contactButton: "Contact Us",
		contactChat: "Live Chat",
		searchResults: "search results for",
		clearResults: "Clear Results",
	},
	fr: {
		contactLabel: "Avez-vous une question sans réponse?",
		contactButton: "Nous contacter",
		contactChat: "Chat en direct",
		searchResults: "Résultats de recherche pour",
		clearResults: "Effacer les résultats",
	},
	es: {
		contactLabel: "¿Tienes una pregunta sin respuesta?",
		contactButton: "Contacta con nosotros",
		contactChat: "Chat en vivo",
		searchResults: "resultados de búsqueda de",
		clearResults: "Borrar resultados",
	},
};

/** Build-time props: the header and footer data plus every glossary term, keyed by language. */
export const getStaticProps = async () => {
	const { props: headerFooterProps } = await allContentfulHeaderCalls();

	return {
		props: {
			...headerFooterProps,
			glossary: await getContentfulGlossary(),
		},
	};
};

/** Renders the glossary in the visitor's language, with its JSON-LD. */
const Page: React.FC<
	HeaderPageProps & { glossary: ProcessedContentfulGlossaryFieldsByLanguage }
> = (props) => {
	const {
		localization: { language },
	} = useApplication();

	const glossaryItemsForLanguage = props.glossary[language];

	// The terms are already filtered to this brand by the fetcher. `GlossaryPage` uses `company`
	// only to pick the breadcrumb.
	const company =
		process.env.NEXT_PUBLIC_COMPANY === "southernToolSteel" ? "Southern Tool Steel" : "Ryerson";

	let glossaryMetaJson: any[] = [
		{
			"@context": "https://schema.org/",
		},
		{
			"@type": ["DefinedTermSet", "Book"],
			"@id": "/metal-resources/references/glossary",
			name: "Metal Resources Glossary",
		},
	];
	// One `DefinedTerm` per term. Its `@id` points at `#<slug>`, but `GlossaryPage` renders no
	// element with that id, so the fragment doesn't land on the term.
	glossaryItemsForLanguage.forEach((glossaryItem) => {
		glossaryMetaJson.push({
			"@type": "DefinedTerm",
			"@id": `/metal-resources/references/glossary#${glossaryItem.slug}`,
			name: glossaryItem.title,
			description: glossaryItem.plaintextDefinition,
			inDefinedTermSet: "/metal-resources/references/glossary",
		});
	});

	// Renders nothing until mounted in the browser, so the exported HTML holds neither the terms
	// nor the JSON-LD; both are added by the client. `components/NextLayouts/RootLayout.tsx`
	// already holds every page back the same way, with `HasMounted`.
	if (!useHasMounted()) return null;

	return (
		<HeaderLayout {...getHeaderData(props)}>
			<MetaHead content={GlossaryMetaObject[language]}>
				<script type="application/ld+json">{JSON.stringify(glossaryMetaJson)}</script>
			</MetaHead>
			<GlossaryPage
				content={GlossaryPageContent[language]}
				hero={GlossaryHeroContent[language]}
				queryData={glossaryItemsForLanguage}
				company={company}
			/>
		</HeaderLayout>
	);
};

export default Page;
